import { memo, useContext } from 'react';
import { Line } from 'react-chartjs-2';
import type { ChartData, ChartOptions } from 'chart.js';
import { dayChartOption, dayChartData } from './dayChartData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { AllInputDataContext, CalculatedDataContext } from '../../../config';
import allStyles from './../AllCharts.module.css';
import styles from './DayChart.module.css';
import { sum } from 'mathjs';

type ToolTipProps = {
  name: string;
  description: string;
};

const ToolTip = memo(({ name, description }: ToolTipProps) => {
  return (
    <div className={styles.chartsTooltip}>
      <FontAwesomeIcon icon={faInfoCircle} className={styles.chartsInfoIcon} />
      <span className={styles.chartsTooltiptext}>
        <p>
          <b>{name}</b>
          <br />
          {description}
        </p>
      </span>
    </div>
  );
});

type Props = {
  dayTypeNumber: number;
  version?: string;
};

const DayChart = memo(({ dayTypeNumber, version }: Props) => {
  const allInputData = useContext(AllInputDataContext);
  const calculatedData = useContext(CalculatedDataContext);

  const displayAxis = () => {
    if (window.innerWidth < 600) {
      return true;
    } else if (
      dayTypeNumber === 0 ||
      dayTypeNumber === 4 ||
      version === 'educational'
    ) {
      return true;
    } else return false;
  };

  const chartData = dayChartData({
    dayTypeNumber,
    allInputData,
    calculatedData
  });

  const chartOptions = dayChartOption({
    dayTypeNumber,
    allInputData,
    displayAxis
  });
  const isBlackout = () => {
    return (
      calculatedData.annualConst.averageGenPerDay[
        calculatedData.dataIndex.blackout
      ][dayTypeNumber] > 0 ||
      sum(
        calculatedData.dayChartData[dayTypeNumber][
          calculatedData.dataIndex.blackout
        ]
      ) > 0
    );
  };

  // `allDayData` entries carry `name`/`description` at runtime that are not part
  // of the typed `AllDayDatum` model; read them through a narrow local shape.
  const dayInfo = allInputData.allDayData[dayTypeNumber] as {
    name: string;
    description: string;
  };

  return (
    <div
      className={
        version === 'educational' ? styles.simpleAverageChart : styles.dayChart
      }
    >
      <div
        className={`${allStyles.chartInnerCard} ${
          isBlackout() ? styles.blackoutGlow : ''
        }`}
      >
        <ToolTip name={dayInfo.name} description={dayInfo.description} />

        {/* The helper returns the generic ChartData/ChartOptions; narrow to
            the line chart at this library boundary. */}
        <Line
          data={chartData as ChartData<'line'>}
          options={chartOptions as ChartOptions<'line'>}
        />
      </div>
    </div>
  );
});
export default DayChart;
